import { useMemo } from 'react';
import { Slot } from '../components/ObjectCard';
import { usePresentation } from '../presentation/PresentationContext';
import { useScene } from './SceneContext';

/** Emphasizes public names and known resources without changing the host's log text. */
export function RichLogLine({ message }: { message: string }) {
  const { table, seatColor } = useScene();
  const presentation = usePresentation();
  const tokens = useMemo(() => [
    ...table.seats.map(seat => ({ text: seat.label, kind: 'seat' as const, key: seat.id })),
    ...table.resources.map(resource => ({ text: resource.label, kind: 'resource' as const, key: resource.key })),
  ].filter(token => token.text.length > 1).sort((a, b) => b.text.length - a.text.length), [table.seats, table.resources]);
  const pieces: React.ReactNode[] = [];
  let cursor = 0;
  while (cursor < message.length) {
    const next = tokens.map(token => ({ token, at: message.toLocaleLowerCase().indexOf(token.text.toLocaleLowerCase(), cursor) }))
      .filter(match => match.at >= 0 && (match.at === 0 || !/[\p{L}\p{N}]/u.test(message[match.at - 1]!)) && (match.at + match.token.text.length === message.length || !/[\p{L}\p{N}]/u.test(message[match.at + match.token.text.length]!)))
      .sort((a, b) => a.at - b.at || b.token.text.length - a.token.text.length)[0];
    if (!next) { pieces.push(message.slice(cursor)); break; }
    if (next.at > cursor) pieces.push(message.slice(cursor, next.at));
    const text = message.slice(next.at, next.at + next.token.text.length);
    if (next.token.kind === 'seat') pieces.push(<strong key={next.at} className="log-seat" style={{ color: seatColor(next.token.key) }}>{text}</strong>);
    else {
      const icon = presentation.icon(next.token.key);
      pieces.push(<span key={next.at} className="log-resource">{icon && <span className="log-resource-icon" aria-hidden="true"><Slot value={icon} /></span>}{text}</span>);
    }
    cursor = next.at + next.token.text.length;
  }
  return <>{pieces}</>;
}
